import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, MapPin, Clock, ExternalLink, FileText } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';
import { useAuth } from '../../contexts/Authcontext';
import { careersAPI } from '../../services/api';

const MyApplications = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchApplications();
  }, [user]);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await careersAPI.getMyApplications();
      setApplications(data || []);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'screening':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'interview':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'offer':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'applied':
        return 'Application Submitted';
      case 'screening':
        return 'Under Review';
      case 'interview':
        return 'Interview Scheduled';
      case 'offer':
        return 'Offer Extended';
      case 'rejected':
        return 'Not Selected';
      default:
        return status;
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'text-blue-600 hover:text-blue-700',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'text-cyan-400 hover:text-cyan-300',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'text-purple-400 hover:text-purple-300',
    };
  };

  const classes = getThemeClasses();

  if (loading) {
    return (
      <div className={`min-h-screen ${classes.bg} flex items-center justify-center pt-20`}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className={`mt-4 ${classes.subtext}`}>Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${classes.bg} pt-20 pb-12`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-2`}>
            My Applications
          </h1>
          <p className={`${classes.subtext}`}>
            Track the status of your job applications
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500">
            {error}
          </div>
        )}

        {applications.length === 0 ? (
          <div className={`${classes.cardBg} rounded-2xl p-12 border ${classes.cardBorder} text-center`}>
            <Briefcase className={`w-16 h-16 ${classes.subtext} mx-auto mb-4`} />
            <h2 className={`text-2xl font-bold ${classes.text} mb-2`}>No Applications Yet</h2>
            <p className={`${classes.subtext} mb-6`}>
              You haven't applied to any positions yet. Browse our open positions to get started!
            </p>
            <Link
              to="/careers"
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              Browse Jobs
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((application) => (
              <div
                key={application.id}
                className={`${classes.cardBg} rounded-xl p-6 border ${classes.cardBorder} hover:shadow-lg transition-all`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className={`text-xl font-bold ${classes.text} mb-2`}>
                          {application.job_title || 'Job Title'}
                        </h3>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <span className={`flex items-center gap-1 ${classes.subtext}`}>
                            <Briefcase className="w-4 h-4" />
                            {application.department || 'Department'}
                          </span>
                          <span className={`flex items-center gap-1 ${classes.subtext}`}>
                            <MapPin className="w-4 h-4" />
                            {application.location || 'Location'}
                          </span>
                          <span className={`flex items-center gap-1 ${classes.subtext}`}>
                            <Clock className="w-4 h-4" />
                            Applied {new Date(application.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(application.status)}`}>
                        {getStatusText(application.status)}
                      </span>
                    </div>

                    {application.notes && (
                      <div className={`mt-3 p-3 ${theme === 'light' ? 'bg-gray-50' : 'bg-slate-900/50'} rounded-lg`}>
                        <p className={`text-sm ${classes.subtext}`}>
                          <strong>Note from recruiter:</strong> {application.notes}
                        </p>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-3">
                      {application.resume_url && (
                        <a
                          href={application.resume_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-4 py-2 border ${classes.cardBorder} rounded-lg ${classes.accent} transition-colors`}
                        >
                          <FileText className="w-4 h-4" />
                          View Resume
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <Link
                        to={`/application/${application.id}`}
                        className={`flex items-center gap-2 px-4 py-2 border ${classes.cardBorder} rounded-lg ${classes.accent} transition-colors`}
                      >
                        View Details
                      </Link>
                    </div>
                  </div>

                  {/* Application Timeline */}
                  <div className="lg:w-64">
                    <h4 className={`text-sm font-semibold ${classes.text} mb-3`}>
                      Application Progress
                    </h4>
                    <div className="space-y-2">
                      {['applied', 'screening', 'interview', 'offer'].map((step, index) => {
                        const isActive = ['applied', 'screening', 'interview', 'offer'].indexOf(application.status) >= index;
                        const isCurrent = application.status === step;

                        return (
                          <div key={step} className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              isActive ? (isCurrent ? 'bg-purple-600' : 'bg-green-500') : 'bg-gray-300'
                            }`} />
                            <span className={`text-sm ${
                              isActive ? classes.text : classes.subtext
                            }`}>
                              {getStatusText(step)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;